import { addDays } from '@gk/contracts'
import { formatLocal, localDate, localToInstant } from '../lib/time.ts'
import type { Pending } from '../sync/merge.ts'
import { intervalBounds, type WorkIntervalRow } from './work.ts'

/** Pure helpers of the time screens (M8): what a person typed -> a span the server will accept, one person's time grouped by local day, and where a review item points.
 *  Nothing here decides what counts, what overlaps or what is paid: the server does; this only mirrors its simplest refusals so a form can say so before sending. */

// ---- a span typed in a form ---------------------------------------------------------------------------------------------------------
export const MAX_SPAN_MS = 24 * 3_600_000
/** the server accepts an end up to five minutes ahead of its own clock (a phone a little fast) */
export const FUTURE_SLACK_MS = 5 * 60_000
export type SpanProblem = 'missing' | 'not_after' | 'future' | 'too_long'
export interface Span { startedAt: string; endedAt: string; ms: number; nextDay: boolean }

const DAY = /^\d{4}-\d{2}-\d{2}$/
const CLOCK = /^([01]\d|2[0-3]):[0-5]\d$/

/** Wall-clock times typed on a local day, as instants in the SITE's zone. The end is on the same day, unless it is earlier than the start: then it is the next day
 *  (a night shift). `null` while the day or a time is missing or unreadable. */
export function spanOf(day: string, from: string, to: string, tz: string): Span | null {
  if (!DAY.test(day) || !CLOCK.test(from) || !CLOCK.test(to)) return null
  const nextDay = to < from
  const startedAt = localToInstant(day, from, tz)
  const endedAt = localToInstant(nextDay ? addDays(day, 1) : day, to, tz)
  return { startedAt, endedAt, ms: Date.parse(endedAt) - Date.parse(startedAt), nextDay }
}

/** The same three refusals as `work.manual_entry` / `work.correct`: the end after the start, not in the future (five minutes of slack), at most 24 hours. */
export function spanProblem(span: Span | null, now: number): SpanProblem | null {
  if (!span) return 'missing'
  if (span.ms <= 0) return 'not_after'
  if (Date.parse(span.endedAt) > now + FUTURE_SLACK_MS) return 'future'
  if (span.ms > MAX_SPAN_MS) return 'too_long'
  return null
}

/** "HH:mm" of an instant in the site zone, for a time field that starts from an existing value. */
export const clockOf = (d: Date, tz: string): string => formatLocal(d, tz, 'HH:mm')

// ---- one person's time, by local day ------------------------------------------------------------------------------------------------
export interface TimeEntry {
  row: Pending<WorkIntervalRow>
  /** the local day it started on */
  day: string
  from: Date
  /** null while the timer runs */
  to: Date | null
  ms: number
  running: boolean
  /** corrected: a later interval stands in for it */
  replaced: boolean
  /** shown in the day's total: not replaced, not rejected (the same rule as the timer page); whether it is paid is the server's business */
  counts: boolean
}
export interface DayGroup { day: string; entries: TimeEntry[]; totalMs: number; pendingMs: number }

export function toEntry(row: Pending<WorkIntervalRow>, tz: string, now: number): TimeEntry | null {
  const { from, to } = intervalBounds(row.during)
  if (!from) return null
  const replaced = !!row.superseded_by_id
  return {
    row, day: localDate(from, tz), from, to, ms: Math.max(0, (to?.getTime() ?? now) - from.getTime()), running: !to && !replaced, replaced,
    counts: !replaced && row.review_state !== 'rejected',
  }
}
const newestFirst = (a: TimeEntry, b: TimeEntry) => b.from.getTime() - a.from.getTime() || a.row.id.localeCompare(b.row.id)

export interface DayOptions {
  tz: string; now: number; today: string
  /** how many local days back, today included */
  days: number
  /** whose time; `everybody` ignores it */
  personId: string | null; everybody: boolean
  showReplaced: boolean
  /** replaced periods shown anyway: the one the person has just corrected stays in place, so its message does (the list catches up on the next sync) */
  keep: ReadonlySet<string>
  /** leave out what waits for review (the screen lists those on their own) */
  skipPending: boolean
}

/** Newest day first, newest period first inside a day; a day with nothing to show is left out. */
export function buildDays(rows: ReadonlyArray<Pending<WorkIntervalRow>>, o: DayOptions): DayGroup[] {
  const first = addDays(o.today, 1 - o.days)
  const byDay = new Map<string, TimeEntry[]>()
  for (const r of rows) {
    if (!o.everybody && r.person_id !== o.personId) continue
    const e = toEntry(r, o.tz, o.now)
    if (!e || e.day < first || (e.replaced && !o.showReplaced && !o.keep.has(e.row.id)) || (o.skipPending && e.row.review_state === 'pending' && (!e.replaced || o.keep.has(e.row.id)))) continue
    byDay.set(e.day, [...(byDay.get(e.day) ?? []), e])
  }
  return [...byDay].sort(([a], [b]) => b.localeCompare(a)).map(([day, list]) => {
    const entries = [...list].sort(newestFirst)
    const sum = (f: (e: TimeEntry) => boolean) => entries.filter(f).reduce((n, e) => n + e.ms, 0)
    return { day, entries, totalMs: sum((e) => e.counts), pendingMs: sum((e) => e.counts && e.row.review_state === 'pending') }
  })
}

/** Everything that waits for the head greenkeeper's decision, whatever its age (an old one must not hide), newest first. */
export function pendingEntries(rows: ReadonlyArray<Pending<WorkIntervalRow>>, tz: string, now: number, keep: ReadonlySet<string> = new Set()): TimeEntry[] {
  return rows.filter((r) => r.review_state === 'pending' && (!r.superseded_by_id || keep.has(r.id))).map((r) => toEntry(r, tz, now)).filter((e): e is TimeEntry => !!e).sort(newestFirst)
}

// ---- the review queue ---------------------------------------------------------------------------------------------------------------
export interface ReviewItem {
  id: string; kind: string; code: string | null; severity: 'low' | 'medium' | 'high'; created_at: string
  entity_type: string | null; entity_id: string | null; details: Record<string, unknown> | null
  person: string | null; job_title: { fr?: string; en?: string } | null; asset_tag: string | null
  interval: { from: string; to: string | null; source: string } | null
}
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i
const asId = (v: unknown): string | null => (typeof v === 'string' && UUID.test(v) ? v : null)

/** The job and the machine an item is about, when its own record says so: an item ABOUT a job or a machine, the ids in its details, or (the head's device holds the
 *  whole site's work) the job of the session or interval it is about, or (M25-F) of the settings confirmation it is about (`confirmationJob`: confirmation id → job id,
 *  from the device's own copy of ops.job_settings_confirmation). Never guessed: no id, no link. */
export function reviewTarget(
  item: Pick<ReviewItem, 'entity_type' | 'entity_id' | 'details'>,
  sessionJob: ReadonlyMap<string, string | null>, intervalSession: ReadonlyMap<string, string>, confirmationJob: ReadonlyMap<string, string> = new Map(),
): { jobId: string | null; assetId: string | null } {
  const d = item.details ?? {}
  const own = asId(item.entity_id)
  const viaSession = (sessionId: string | undefined): string | null => (sessionId ? sessionJob.get(sessionId) ?? null : null)
  const jobId = item.entity_type === 'ops.job' ? own
    : item.entity_type === 'ops.work_session' ? asId(d.job_id) ?? viaSession(own ?? undefined)
    : item.entity_type === 'ops.work_interval' ? viaSession(own ? intervalSession.get(own) : undefined)
    : item.entity_type === 'ops.job_settings_confirmation' ? (own ? confirmationJob.get(own) ?? null : null)               // M25-F: the device's copy of the row says which job
    : asId(d.job_id)
  return { jobId, assetId: item.entity_type === 'fleet.asset' ? own : asId(d.asset_id) }
}

/** An interval's bounds as two short local texts: the end carries its date only when it is not the start's day. `to` is null while the timer runs. */
export function spanText(from: string, to: string | null, tz: string): { from: string; to: string | null } {
  const f = formatLocal(from, tz, 'dd/MM HH:mm')
  if (!to) return { from: f, to: null }
  return { from: f, to: localDate(from, tz) === localDate(to, tz) ? formatLocal(to, tz, 'HH:mm') : formatLocal(to, tz, 'dd/MM HH:mm') }
}
